<template>
    <layout>
        <template v-slot:header>
            <AppHead title="MTD Checkout" />
            <div class="admin-header-container">
                <h2 class="mr-3">Checkout - {{ mtd.company_name }}</h2>
            </div>
        </template>

        <template v-slot:breadcrumbs>
            <BreadCrumbs activeItem="MTD Checkout" :crumbs="{ home: 'Dashboard' }" />
        </template>

        <template v-slot:default>
            <section class="main-section rounded p-0 p-md-3 my-2 vl-parent">
                <LoadingOverlay v-model:active="loading" :is-full-page="true" />

                <div class="d-flex flex-column flex-lg-row align-items-lg-center">
                    <p class="mr-3 text-secondary mb-2"><i class="fas fa-user mr-1"></i>{{ mtd.name }}</p>
                    <p class="mr-3 text-secondary mb-2"><i class="fas fa-envelope mr-1"></i>
                        <Email v-model="mtd.email" />
                    </p>
                    <p class="text-secondary mb-2"><i class="fas fa-phone mr-1"></i>
                        <Phone v-model="mtd.phone" />
                    </p>
                </div>


                <div class="row">
                    <section class="col-12 col-xl-6">
                        <card headerStyle="primary" title="Shipping Address" class="mb-3">
                            <template v-slot:header>
                                <div class="form-check float-right">
                                    <input class="form-check-input" type="checkbox"
                                        id="customer-direct-destination-checkbox"
                                        v-model="formAddress.customer_direct_destination">
                                    <label class="form-check-label text-white" for="customer-direct-destination-checkbox">
                                        Customer Direct Destination
                                    </label>
                                </div>
                            </template>

                            <Multiselect v-model="formPayment.ship_address_id" :options="filteredAddresses"
                                :searchable="true" :canDeselect="false" class="admin-multiselect" id="addresses_select" />

                            <jet-add-button @click="showAddress" modalTarget="#addressModal">Add Address</jet-add-button>
                        </card>

                        <card v-if="!formPayment.isPromotion && has_mtd_shipping" headerStyle="primary"
                            title="Shipping Method" class="mb-3">
                            <div v-if="!formPayment.ship_address_id"><span>Please select a shipping address</span></div>
                            <div v-else-if="methods.length === 0">
                                <i class="fa fa-spinner fa-spin"></i> Loading shipping rates...
                            </div>
                            <div v-else>
                                <div class="method form-check" v-for="method in methods" :key="method.service_id">
                                    <input type="radio" name="ship_method" class="form-check-input"
                                        :id="`shipping_method_${method.service_id}`" :value="method.service_id"
                                        v-model="formPayment.ship_method" />

                                    <label :for="`shipping_method_${method.service_id}`">
                                        <span v-if="method.cost > 0">{{ formatDollars(method.cost) }}</span>
                                        <span v-else>FREE</span> {{ method.label }}
                                    </label>
                                </div>
                            </div>
                        </card>

                        <div class="mt-3" v-if="!formPayment.isPromotion">
                            <PaymentMethod :mtd_instant_payment_only="mtd.mtd_instant_payment_only"
                                :payment_intents="payment_intents" :paymentBalance="cartTotals.balance"
                                :current_account_balance="cartTotals.current_account_balance" :form="formPayment"
                                :cards="cards" :banks="banks" :for_user="for_user" @setPaymentMethod="setPaymentMethod"
                                @setPaymentIntent="setPaymentIntent" />
                        </div>
                    </section>

                    <section class="col-12 col-xl-6">
                        <CheckoutTable :items="items" :lineItems="lineItems" :isWholesale="true"
                            :updateItemRoute="route('admin.mtd.cart.updateItem', this.mtd.id)"
                            :deleteItemRoute="route('admin.mtd.cart.deleteItem', this.mtd.id)"
                            :setItemDiscountRoute="route('admin.mtd.cart.setItemDiscount', this.mtd.id)"
                            :clearItemDiscountRoute="route('admin.mtd.cart.removeItemDiscount', this.mtd.id)"
                            :isPromotion="formPayment.isPromotion" @onUpdate="() => this.fetchCheckoutData()" />

                        <CheckoutTotals :lineItems="lineItems" :cartTotals="cartTotals" :isWholesale="true"
                            :isPromotion="formPayment.isPromotion" />
                        <div class="mt-2 mb-2 bg-primary card-header p-2 pr-4">
                            <div class="form-check text-right">
                                <input type="checkbox" class="form-check-input" id="is-promotion-checkbox"
                                    v-model="formPayment.isPromotion">
                                <label class="form-check-label text-white" for="is-promotion-checkbox">
                                    Promotion
                                </label>
                            </div>
                        </div>
                    </section>
                </div>

                <div class="bottom-button bg-light d-flex justify-content-end mt-2">
                    <jet-button class="px-5" btnStyle="success" icon="fas fa-shopping-cart"
                        :disable="formPayment.processing" @click.prevent="completeCheckout()">Submit</jet-button>
                </div>
            </section>

            <AddCard :routeUrl="route('admin.mtd.checkout.addCard', this.mtd.id)" :formCard="formAddCard" />

            <AddShippingAddress ref="addressForm" :states="states" @addAddress="addAddress" :formAddress="formAddress" />

            <jet-dialog-modal id="cashModal">
                <template #title>Cash Payment</template>
                <template #content>
                    <i class="fa fa-spinner fa-spin"></i> Waiting for confirmation...
                </template>

                <template #footer>
                    <jet-danger-button class="ml-2" @click="cancelCash">Cancel</jet-danger-button>
                </template>
            </jet-dialog-modal>

            <jet-dialog-modal id="termModal">
                <template #title>Payment Term Request</template>
                <template #content>
                    <i class="fa fa-spinner fa-spin"></i> Waiting for confirmation...
                </template>

                <template #footer>
                    <jet-danger-button class="ml-2" @click="cancelCash">Cancel</jet-danger-button>
                </template>
            </jet-dialog-modal>
        </template>
    </layout>
</template>

<script>
import Cart from '@/Components/Admin/Cart'
import JetLabel from '@/Jetstream/Label'
import Multiselect from '@vueform/multiselect'
import AppHead from "@/Shared/AppHead";
import Layout from '@/Shared/Admin/Layout'
import FlashMessages from '@/Shared/FlashMessages'
import moment from 'moment'
import BreadCrumbs from '@/Shared/BreadCrumbs'
import JetButton from '@/Jetstream/Button'
import JetAddButton from '@/Jetstream/AddButton'
import PaymentMethod from '@/Components/Shop/PaymentMethod'
import CheckoutTable from '@/Components/Shop/CheckoutTable'
import AddCard from '@/Components/Modals/AddCard'
import AddShippingAddress from '@/Components/Modals/AddShippingAddress'
import JetDangerButton from '@/Jetstream/DangerButton'
import JetDialogModal from '@/Jetstream/DialogModal'
import Card from '@/Shared/Card'
import CheckoutTotals from '@/Components/Shop/CheckoutTotals'
import LoadingOverlay from '@/Components/General/LoadingOverlay.vue';

export default {
    components: {
        AppHead,
        Cart,
        JetLabel,
        Multiselect,
        JetButton,
        FlashMessages,
        Layout,
        BreadCrumbs,
        JetAddButton,
        PaymentMethod,
        CheckoutTable,
        AddCard,
        AddShippingAddress,
        JetDangerButton,
        JetDialogModal,
        Card,
        LoadingOverlay,
        CheckoutTotals
    },
    props: {
        mtd: Object,
        addresses: Array,
        payment_intents: Array,
        cards: Array,
        banks: Array,
        ship_address: Object,
        shipping_method_id: String,
        states: Array,
        for_user: Object,
        has_mtd_shipping: Boolean,
        customer_direct_order: Boolean,
        cartTotals: Array,
        accounting_categories: Array,
    },
    data() {
        return {
            filteredAddresses: [],
            loading: true,
            errors: [],
            methods: [],
            items: [],
            lineItems: [],
            cartTotals: [],
            formPayment: this.$inertia.form({
                payment_intent: 'Credit Card',
                stripe_payment_method: null,
                term_id: null,
                ship_address_id: this.ship_address ? this.ship_address.id : null,
                ship_method: this.shipping_method_id,
                user_timezone: this.mtd.user_timezone,
                isPromotion: false,
            }),
            formAddCard: this.$inertia.form({
                card_name: null,
                card_number: null,
                card_exp_month: null,
                card_exp_year: null,
                card_cvc: null,
                card_zip: null,
            }),
            formAddress: this.$inertia.form({
                first_name: null,
                last_name: null,
                address1: null,
                address2: null,
                city: null,
                state: null,
                postcode: null,
                email: null,
                phone: null,
                company_name: null,
                customer_direct_destination: this.customer_direct_order,
            }),
            formUpdateAddress: this.$inertia.form({
                address_id: null
            }),
            formUpdateMethod: this.$inertia.form({
                method_id: null,
                cost: null
            }),
        }
    },
    created() {
        this.moment = moment
    },
    mounted() {
        this.filterAddresses();
        this.fetchCheckoutData();
    },
    computed: {
        days: function () {
            let days = [];
            let date = null;
            for (let i = 0; i < 7; i++) {
                date = moment().day(i);
                days[i] = date.format('dddd')
            }

            return days;
        },
    },
    watch: {
        'ship_address.id': function () {
            this.formPayment.ship_address_id = this.ship_address.id;
        },
        'formPayment.ship_method': function () {
            let method = this.methods.find((method) => method.service_id === this.formPayment.ship_method);

            if (method) {
                this.formUpdateMethod.method_id = method.service_id;
                this.formUpdateMethod.cost = method.cost;

                this.formUpdateMethod.post(this.route('admin.mtd.checkout.updateMethod', this.mtd.id), {
                    preserveScroll: true,
                    onSuccess: () => this.fetchCheckoutData(),
                });
            }
        },
        'formPayment.ship_address_id': function () {
            this.resetErrors();

            if (this.formPayment.ship_address_id !== null && this.formPayment.ship_address_id !== this.ship_address.id) {
                this.formUpdateAddress.address_id = this.formPayment.ship_address_id;
                this.formUpdateAddress.post(this.route('admin.mtd.checkout.updateShippingAddress', this.mtd.id), {
                    preserveScroll: true,
                    onSuccess: () => this.fetchCheckoutData(),
                });
            }
        },
        'formAddress.customer_direct_destination': function () {
            this.filterAddresses();
        }
    },
    methods: {
        resetErrors() {
            this.errors = [];
        },
        setPaymentMethod() {
            this.resetErrors();
        },
        setPaymentIntent(paymentIntent) {
            this.formPayment.payment_intent = paymentIntent;
            this.formPayment.stripe_payment_method = null;
        },
        showAddress() {
            this.resetErrors();
        },
        addAddress() {
            let customer_direct_destination = this.formAddress.customer_direct_destination;

            if (this.formAddress.phone) {
                this.formAddress.phone = this.formAddress.phone.replace(/\D+/g, '');
            }

            this.formAddress.post(this.route('admin.mtd.checkout.addAddress', this.mtd.id), {
                preserveScroll: true,
                onSuccess: () => {
                    this.filterAddresses();
                    this.fetchCheckoutData();
                    this.$refs.addressForm.$refs.address1.$el.value = null
                    this.formAddress.reset()
                    this.formAddress.customer_direct_destination = customer_direct_destination;
                    $('#addressModal').modal('hide');
                }
            });
        },
        cancelCash() {
            $('#cashModal').modal('hide');
        },
        completeCheckout() {
            this.resetErrors();

            if (!this.formPayment.isPromotion) {
                if ((this.order_balance_total > 0 &&
                    (this.formPayment.payment_intent === 'Credit Card' || this.formPayment.payment_intent === 'ACH'))
                    && !this.formPayment.stripe_payment_method
                ) {
                    this.errors[this.errors.length] = "Please select a payment method.";
                    return;
                }

                if (!this.formPayment.ship_address_id) {
                    this.errors[this.errors.length] = "Please select a shipping address.";
                    return;
                }

                if (this.has_mtd_shipping && !this.formPayment.ship_method) {
                    this.errors[this.errors.length] = "Please select a shipping method.";
                    return;
                }
            }

            this.formPayment.post(this.route('admin.mtd.checkout.complete', this.mtd.id));
        },
        filterAddresses() {
            if (this.formAddress.customer_direct_destination) {
                let filteredAddresses = this.addresses.filter(address => address.is_customer_direct_destination);

                // If the selected shipment id is not in the new array, set it to null.
                if (!filteredAddresses.find(address => address.value === this.formPayment.ship_address_id)) {
                    this.formPayment.ship_address_id = null;
                }

                this.filteredAddresses = filteredAddresses;
            } else {
                // If the current session shipment id is null, reset to default.
                if (!this.formPayment.ship_address_id) {
                    this.formPayment.ship_address_id = this.ship_address.id;
                }
                this.filteredAddresses = this.addresses;
            }
        },
        fetchCheckoutData() {
            this.loading = true;
            axios.get(route('admin.mtd.checkout.getData', this.mtd.id))
                .then((resp) => {
                    if (resp.data.success) {
                        this.methods = resp.data.methods;
                        this.items = resp.data.items;
                        this.lineItems = resp.data.line_items;
                        this.cartTotals = resp.data.cartTotals;
                        this.formPayment.payment_amount = parseFloat(resp.data.cartTotals.balance);
                        this.formPayment.ship_method = resp.data.shipping_method_id;
                    }

                    this.loading = false;
                });
        }
    },
}
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
